"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Eye, Package } from "lucide-react";

import {
  getOrderHistory,
  type OrderHistoryResponse,
} from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { toIntlDateLocale, toIntlLocale } from "@/lib/i18n/intl-locale";

import { translateOrderStatus } from "./order-status";

interface OrdersListProps {
  userData: any;
  businessId: string;
  onViewDetails: (orderId: string) => void;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function OrdersList({ userData: _userData, businessId, onViewDetails }: OrdersListProps) {
  const { t, currentLanguage } = usePageTranslation();
  const [orders, setOrders] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ordersTopRef = useRef<HTMLDivElement>(null);

  const perPage = 10;

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, businessId]);

  const fetchOrders = async (page: number) => {
    try {
      setIsLoading(true);
      setError(null);

      // Get access token from storage using auth-utils
      const accessToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      if (!businessId) {
        throw new Error(t("account.businessIdRequired"));
      }

      const response: OrderHistoryResponse = await getOrderHistory(
        accessToken,
        businessId,
        page,
        perPage
      );

      if (response.status && response.data) {
        // Ensure orders is always an array, even if null/undefined
        setOrders(response.data.orders || []);
        setPagination(response.data.pagination);

        // Scroll to top of orders section after successful load
        if (ordersTopRef.current) {
          ordersTopRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      } else {
        throw new Error(t("account.fetchOrdersFailed"));
      }
    } catch (err: any) {
      console.error("Error fetching orders:", err);
      setError(err.message || t("account.loadOrdersFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number, currency: string = "GBP") => {
    return new Intl.NumberFormat(toIntlLocale(currentLanguage), {
      style: "currency",
      currency: currency,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(toIntlDateLocale(currentLanguage), {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      completed:
        "border-brand-success/30 bg-brand-success/10 text-brand-success",
      processing: "border-blue-200 bg-blue-100 text-blue-800",
      pending: "border-brand-warning/40 bg-brand-warning/15 text-brand-fg",
      cancelled: "border-brand-danger/30 bg-brand-danger/10 text-brand-danger",
      confirmed: "border-purple-200 bg-purple-100 text-purple-800",
    };
    return (
      statusColors[status.toLowerCase()] ||
      "border-brand-border bg-brand-fg/5 text-brand-fg"
    );
  };

  const getPaymentStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      paid: "bg-brand-success/10 text-brand-success",
      pending: "bg-brand-warning/15 text-brand-fg",
      failed: "bg-brand-danger/10 text-brand-danger",
    };
    return statusColors[status.toLowerCase()] || "bg-brand-fg/5 text-brand-fg";
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (pagination?.last_page || 1)) {
      setCurrentPage(page);
    }
  };

  if (isLoading && orders.length === 0) {
    return (
      <div className="orders-list">
        <h2
          className="mb-6 text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.myOrders")}
        </h2>
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-list">
        <h2
          className="mb-6 text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.myOrders")}
        </h2>
        <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-8 text-center">
          <p className="text-brand-danger">{error}</p>
          <button
            onClick={() => fetchOrders(currentPage)}
            className="mt-4 rounded-brand bg-brand-danger px-6 py-2 font-semibold text-brand-danger-foreground transition-colors hover:bg-brand-danger/90"
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="orders-list">
        <h2
          className="mb-6 text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.myOrders")}
        </h2>
        <div className="rounded-brand border border-brand-border bg-brand-fg/5 p-8 text-center">
          <Package className="mx-auto mb-4 h-12 w-12 text-brand-muted" />
          <p className="mb-2 text-lg font-semibold text-brand-fg">
            {t("account.noOrdersYet")}
          </p>
          <p className="text-brand-muted">{t("account.noOrdersMessage")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-list">
      <div
        ref={ordersTopRef}
        className="mb-6 flex items-center justify-between"
      >
        <h2
          className="text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.myOrders")}
        </h2>
        {pagination && (
          <p className="text-sm text-brand-muted">
            {t("account.showingOrders", {
              from: pagination.from,
              to: pagination.to,
              total: pagination.total,
            })}
          </p>
        )}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {orders.map((order) => (
          <div
            key={order.id}
            className="order-card rounded-brand-lg border border-brand-border bg-brand-surface p-6 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Order Info */}
              <div className="flex-1">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-brand-fg">
                    {t("account.orderNumberTitle", {
                      number: order.order_number,
                    })}
                  </h3>
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(order.order_status)}`}
                  >
                    {translateOrderStatus(t, order.order_status)}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${getPaymentStatusColor(order.payment_status)}`}
                  >
                    {translateOrderStatus(t, order.payment_status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
                  <div>
                    <p className="text-brand-muted">
                      {t("account.orderNumber")}
                    </p>
                    <p className="font-medium text-brand-fg" dir="ltr">
                      {order.order_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-brand-muted">{t("account.date")}</p>
                    <p className="font-medium text-brand-fg">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-brand-muted">{t("account.items")}</p>
                    <p className="font-medium text-brand-fg">
                      {order.items_count}
                    </p>
                  </div>
                  <div>
                    <p className="text-brand-muted">{t("account.payment")}</p>
                    <p className="font-medium text-brand-fg capitalize">
                      {order.payment_method.replace("-", " ")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total and Action */}
              <div className="flex items-center justify-between gap-4 border-t border-brand-border pt-4 lg:flex-col lg:border-t-0 lg:border-s lg:pt-0 lg:ps-6">
                <div className="text-start lg:text-end">
                  <p className="text-sm text-brand-muted">{t("cart.total")}</p>
                  <p className="text-xl font-bold text-brand-primary">
                    {formatPrice(order.total, order.currency)}
                  </p>
                </div>
                <button
                  onClick={() => onViewDetails(order.id)}
                  className="flex items-center gap-2 rounded-brand bg-brand-primary px-4 py-2 font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
                >
                  <Eye className="h-4 w-4" />
                  <span>{t("common.viewDetails")}</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="mt-6 flex items-center justify-between rounded-brand border border-brand-border bg-brand-surface p-4">
          <div className="text-sm text-brand-muted">
            {t("account.pageOf", {
              current: pagination.current_page,
              last: pagination.last_page,
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="flex items-center gap-1 rounded-brand border border-brand-border px-3 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" />
              {t("account.previous")}
            </button>

            {/* Page Numbers */}
            <div className="hidden items-center gap-1 md:flex">
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                .filter((page) => {
                  // Show first, last, current, and adjacent pages
                  return (
                    page === 1 ||
                    page === pagination.last_page ||
                    Math.abs(page - currentPage) <= 1
                  );
                })
                .map((page, index, array) => {
                  // Add ellipsis
                  const prevPage = array[index - 1];
                  const showEllipsis = prevPage && page - prevPage > 1;

                  return (
                    <div key={page} className="flex items-center gap-1">
                      {showEllipsis && (
                        <span className="px-2 text-brand-muted">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(page)}
                        disabled={isLoading}
                        className={`h-10 w-10 rounded-brand text-sm font-medium transition-colors ${
                          page === currentPage
                            ? "bg-brand-primary text-brand-primary-foreground"
                            : "border border-brand-border text-brand-fg hover:bg-brand-fg/5"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        {page}
                      </button>
                    </div>
                  );
                })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.last_page || isLoading}
              className="flex items-center gap-1 rounded-brand border border-brand-border px-3 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("account.next")}
              <ChevronRight className="h-4 w-4 rtl:-scale-x-100" />
            </button>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && orders.length > 0 && (
        <div className="mt-4 flex items-center justify-center rounded-brand border border-brand-primary/30 bg-brand-primary/5 p-4">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-primary" />
          <span className="ms-3 text-sm font-medium text-brand-primary">
            {t("account.loadingOrders")}
          </span>
        </div>
      )}
    </div>
  );
}
